import React, { createContext } from 'react'
import { generateClient } from 'aws-amplify/api'
import { v4 as uuid } from 'uuid'
import {
  createMatch,
  createNoMatch as createNoMatchMutation,
  createProfileMatch,
  completeProofUpload as completeProofUploadMutation,
  assignTask as assignTaskMutation,
  unassignTask as unassignTaskMutation,
  submitMatchInventory as submitMatchInventoryMutation,
  completeProofUploadInventory as completeProofUploadInventoryMutation,
  submitCreateRecord as submitCreateRecordMutation,
  submitMatchRecord as submitMatchRecordMutation,
  executeTool as executeToolMutation,
  putManualTaskNote,
  putOnHoldTask as putOnHoldTaskMutation,
  resumeTask as resumeTaskMutation,
  submitClaimsAmended as submitClaimsAmendedMutation,
} from '../graphql/mutations'
import {
  getTicketV2 as getTicketQuery,
  getDocumentation as getDocumentationQuery,
  getTools as getToolsQuery,
  queryManualTaskNotes,
} from '../graphql/queries'
import { listTicketsV2 as listTicketsQuery } from '../graphql/customQueries'

const ApiContext = createContext()
const API = generateClient()
export default ApiContext

function ContextState () {
  const getTicketSummaries = async ({ status = 'PENDING', reverseOrder = true, lastKey }) => {
    console.log('getting tickets', status, reverseOrder, lastKey)
    const response = await API.graphql({
      query: listTicketsQuery,
      variables: {
        input: {
          status,
          limit: 10,
          reverseOrder,
          lastKey,
        },
      },
    })
    console.log('List Tickets', response.data.listTicketsV2)
    return response.data.listTicketsV2
  }

  const getTicket = async (taskId) => {
    const response = await API.graphql({
      query: getTicketQuery,
      variables: {
        taskId,
      },
    })
    console.log('Get Ticket', response)
    return response.data.getTicketV2
  }

  const assertSuccess = ({ result }) => {
    if (result === 'Cannot Process Task - Not Owner') {
      throw new Error(result)
    }
  }

  const createNewMatch = async (data) => {
    const response = await API.graphql({
      query: createMatch,
      variables: {
        input: data,
      },
    })
    console.log('Create New Match', response.data.createMatch)
    assertSuccess(response.data.createMatch)
    return response
  }

  const createMatchingProfile = async (data) => {
    const response = await API.graphql({ query: createProfileMatch, variables: { input: data } })
    console.log('Create Matching Profile', response.data.createProfileMatch)
    assertSuccess(response.data.createProfileMatch)
    return response.data.createProfileMatch
  }

  const createNoMatch = async (data) => {
    const response = await API.graphql({ query: createNoMatchMutation, variables: { input: data } })
    console.log('Create No Match', response.data.createNoMatch)
    assertSuccess(response.data.createNoMatch)

    return response
  }

  const uploadDocument = async ({
    uploadUrl,
    contentType,
    data,
  }) => {
    console.log('uploading document', uploadUrl, contentType, data)
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'content-type': contentType,
      },
      body: data,
    })
    console.log('Done uploading', response)
    return 'Done'
  }

  const completeProofUpload = async (data) => {
    const response = await API.graphql({
      query: completeProofUploadMutation,
      variables: { input: data },
    })
    console.log('Complete Proof', response.data.completeProofUpload)

    return response
  }

  const assignTask = async ({ taskId, type }) => {
    console.log('got taskId', taskId, type)
    const operation = type === 'assign' ? assignTaskMutation : unassignTaskMutation
    const response = await API.graphql({
      query: operation,
      variables: {
        taskId,
      },
    })
    console.log('got response', response)
    return (type === 'assign') ? response.data.assignTask : response.data.unassignTask
  }

  const submitMatchInventory = async (input) => {
    const response = await API.graphql({
      query: submitMatchInventoryMutation,
      variables: { input },
    })
    assertSuccess(response.data.submitMatchInventory)
    console.log('match submitted', response)

    return response
  }

  const submitClaimsAmended = async (input) => {
    const response = await API.graphql({
      query: submitClaimsAmendedMutation,
      variables: { input },
    })

    assertSuccess(response.data.submitClaimsAmended)
    console.log('submit claims amended', response.data.submitClaimsAmended)
    return response.data.submitClaimsAmended
  }
  const submitCreateRecord = async (input) => {
    const response = await API.graphql({
      query: submitCreateRecordMutation,
      variables: { input },
    })

    assertSuccess(response.data.submitCreateRecord)
    console.log('match submitted', response.data.submitCreateRecord)
    return response.data.submitCreateRecord
  }

  const submitMatchRecord = async (input) => {
    const response = await API.graphql({
      query: submitMatchRecordMutation,
      variables: { input },
    })

    assertSuccess(response.data.submitMatchRecord)
    console.log('match submitted', response.data.submitMatchRecord)
    return response.data.submitMatchRecord
  }

  const completeProofUploadInventory = async (input) => {
    const response = await API.graphql({
      query: completeProofUploadInventoryMutation,
      variables: { input },
    })
    console.log('complete proof upload submitted', response)

    return response
  }

  const getDocumentation = async (docPath) => {
    const response = await API.graphql({
      query: getDocumentationQuery,
      variables: {
        docPath,
      },
    })
    return response.data.getDocumentation
  }

  const getTools = async () => {
    const response = await API.graphql({ query: getToolsQuery })
    return response.data.getTools
  }

  const executeTool = async (input) => {
    const response = await API.graphql({ query: executeToolMutation, variables: { input } })
    return response.data.executeTool
  }

  const getTaskNotes = async (taskId) => {
    const response = await API.graphql({
      query: queryManualTaskNotes,
      variables: { taskId },
    })
    return response.data.queryManualTaskNotes
  }

  const submitTaskNote = async ({ taskId, noteText }) => {
    const noteId = uuid()
    const response = await API.graphql({
      query: putManualTaskNote,
      variables: {
        input: {
          noteId,
          taskId,
          noteText,
        },
      },
    })
    return response.data.putManualTaskNote
  }

  const putOnHoldTask = async (taskId, userText = null) => {
    const response = await API.graphql({
      query: putOnHoldTaskMutation,
      variables: {
        taskId,
        userText,
      },
    })
    return response.data.putOnHoldTask
  }

  const resumeTask = async (taskId) => {
    const response = await API.graphql({
      query: resumeTaskMutation,
      variables: {
        taskId,
      },
    })
    return response.data.resumeTask
  }

  return {
    createNewMatch,
    createMatchingProfile,
    getTicketSummaries,
    getTicket,
    createNoMatch,
    uploadDocument,
    completeProofUpload,
    assignTask,
    submitMatchInventory,
    completeProofUploadInventory,
    submitClaimsAmended,
    submitCreateRecord,
    submitMatchRecord,
    getDocumentation,
    getTools,
    executeTool,
    getTaskNotes,
    submitTaskNote,
    putOnHoldTask,
    resumeTask,
  }
}

const ApiContextProvider = (props) => {
  const state = ContextState()

  return (
    <ApiContext.Provider value={state}>
      {props.children}
    </ApiContext.Provider>
  )
}

export { ApiContext, ApiContextProvider }
